<template>
  <view-wrapper
    :show-back-button="false"
    name="QuickPrint"
    :loading="loading"
    loading-text="Loading QuickPrint"
  >
    <div class="float-right font-semibold">
      <span v-if="!!message">
        {{ message }}
      </span>
    </div>

    <div class="w-64">
      <grafokett-input
        labelPlaceholder="Scan Id"
        class="normalize-placeholder"
        v-model="scanId"
        @enterPressed="quickPrint"
        type="text"
      >
      </grafokett-input>
    </div>

    <filter-and-sort-box :data="articles" :headers="headers" :useSearch="false">
      <template v-slot:d="{ d }">
        <td
          class="relative py-1"
          v-for="(key, index) in getKeys(d)"
          :key="key + index"
        >
          <div
            class="w-full py-4 pl-4 bg-primary-light"
            :class="{
              'rounded-l': index === 0,
              'rounded-r': Object.keys(headers).length - 1 === index,
            }"
          >
            <span
              v-if="key == 'externalIdentifier'"
              class="inline-block w-10/12"
              >{{ d[key] ? d[key] : "" }}</span
            >
            <span v-if="key == 'name'" class="inline-block w-10/12">{{
              d[key] ? d[key] : ""
            }}</span>
          </div>
        </td>
      </template>
    </filter-and-sort-box>
  </view-wrapper>
</template>

<script>
import { interval, from, of } from "rxjs";
import {
  startWith,
  switchMap,
  catchError,
  map,
  distinctUntilChanged,
} from "rxjs/operators";

import GrafokettInput from "../../components/forms/Input.vue";
import FilterAndSortBox from "../../components/FilterAndSortBox";
import { sendToPrinterService } from "../../utils/printerHelpers";

export default {
  components: {
    GrafokettInput,
    FilterAndSortBox,
  },
  data: () => ({
    loading: false,
    message: "",
    scanId: null,
    articles: [],
    polling$: null,
    headers: {
      name: "Article name",
      Id: "External Id",
    },
  }),
  computed: {
    title() {
      return "QuickPrint";
    },
    printQueues() {
      return this.$store.getters["SETTINGS/PRINT_QUEUES"];
    },
  },
  methods: {
    async getArticles() {
      this.loading = true;
      this.setScanIdFocus();
    },
    async quickPrint() {
      let article = this.articles.find(
        article => article.externalIdentifier == this.scanId
      );

      this.scanId = "";

      if (!article) {
        this.message = `Id ${this.scanId} not found`;
        return;
      }

      var printerId = null;

      if (
        article.assignedPrinterIds &&
        article.assignedPrinterIds.length === 0
      ) {
        this.message = `Article ${article.name} has no assigned Printer`;
        return;
      }

      if (
        article.assignedPrinterIds &&
        article.assignedPrinterIds.length !== 1
      ) {
        this.message = `Article ${
          article.name
        } has more than one assigned Printer`;
        return;
      }

      if (
        article.assignedPrinterIds &&
        article.assignedPrinterIds.length === 1
      ) {
        printerId = article.assignedPrinterIds[0];
      }

      if (!this.printQueues) {
        await this.$store.dispatch("SETTINGS/GET_PRINT_QUEUES");
      }

      await this.$store.dispatch("PRINTJOBS/CREATE_PRINTJOB");

      try {
        this.message = "Sending to printer...";
        var computerName = this.printQueues && this.printQueues.computerName;
        const response = await this.$store.$api.post(
          "/api/printjobs/article/quickprint",
          {
            articleId: article.articleId,
            labelId: article.labelId,
            printerId: printerId,
            computerName: computerName,
            quantity: 1,
            printJobId: this.$store.getters["PRINTJOBS/PRINTJOB_ID"],
          }
        );

        const printFile = response.data.data;
        const printerHelperUri = this.$store.getters[
          "SETTINGS/PRINTER_HELPER_URI"
        ];

        await sendToPrinterService(printerHelperUri, {
          targetAddress: printFile.targetIpAddress,
          port: printFile.targetPort,
          targetPrintQueue: printFile.targetPrintQueue,
          printerFileUrl: printFile.printFileName,
          useLocalPrinterDriver: printFile.useLocalPrinterDriver,
          pdfAsBase64String: printFile.pdfAsBase64String,
          orientation: printFile.orientation,
          scale: printFile.scale,
          side: printFile.side,
          paperSize: printFile.paperSize,
          customPaperSize: printFile.customPaperSize,
        });
      } catch (error) {
        this.message = `Error: ${error.data}`;
        await this.$store.dispatch("PRINTJOBS/UPDATE_PRINTJOB_STATUS", {
          status: "Error",
          message: this.message,
        });
        return;
      }

      this.message = "Sending to printer... OK";
      setTimeout(() => (this.message = ""), 1000);

      await this.$store.dispatch("PRINTJOBS/UPDATE_PRINTJOB_STATUS", {
        status: "Processed",
      });
    },
    setScanIdFocus() {
      document.querySelectorAll("input[type=text]")[0].focus();
    },
    getKeys(order) {
      return Object.keys(order).filter(key => {
        return key == "externalIdentifier" || key == "name";
      });
    },
    dateFormat(iso8601string) {
      let regexp = /(?<date>\d{4}-\d{2}-\d{2})T(?<time>\d{2}:\d{2})/;
      let groups = iso8601string.match(regexp).groups;
      return groups.date + " " + groups.time;
    },
    printArticle(article) {
      this.$router.push("/print-article/" + article.id);
    },
  },
  async mounted() {
    await this.getArticles();
    this.loading = false;
    this.polling$ = interval(10 * 1000)
      .pipe(
        startWith(-1),
        switchMap(() =>
          from(this.$store.$api.get("/api/articles/quickprint")).pipe(
            catchError(error => of({ error: error.message }))
          )
        ),
        map(s => {
          return { data: s.data.data || null, error: s.error || null };
        }),
        distinctUntilChanged()
      )
      .subscribe(resp => (this.articles = resp.data));

    let printQueues = this.$store.getters["SETTINGS/PRINT_QUEUES"];
    if (!printQueues) {
      await this.$store.dispatch("SETTINGS/GET_PRINT_QUEUES");
      printQueues = this.$store.getters["SETTINGS/PRINT_QUEUES"];
    }
  },
  beforeDestroy() {
    this.polling$.unsubscribe();
  },
};
</script>
